import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  Upload,
  Eye,
  FileText,
  Check,
  X,
  ShieldCheck,
  Download,
  Search,
} from "lucide-react";
import { useParams } from "react-router-dom";

const DocumentsAdmin = ({ studentId: propStudentId }) => {
  const { studentId: paramStudentId } = useParams();
  const studentId = propStudentId || paramStudentId;

  const [documents, setDocuments] = useState({
    idOrPassport: { file: null, documentId: null, status: "Status", fileName: null },
    proofOfFunding: { file: null, documentId: null, status: "Status", fileName: null },
    nextOfKinID: { file: null, documentId: null, status: "Status", fileName: null },
    proofOfResidence: { file: null, documentId: null, status: "Status", fileName: null },
  });

  const [previewFile, setPreviewFile] = useState(null);
  const [previewType, setPreviewType] = useState(null);

  const fileRefs = {
    idOrPassport: useRef(),
    proofOfFunding: useRef(),
    nextOfKinID: useRef(),
    proofOfResidence: useRef(),
  };

  useEffect(() => {
    if (!studentId) return;

    const fetchDocuments = async () => {
      try {
        const res = await axios.get(
          `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/StudentDocuments/${studentId}`
        );

        setDocuments(prev => {
          const updated = { ...prev };
          res.data.forEach(doc => {
            if (updated[doc.documentType]) {
              updated[doc.documentType] = {
                ...updated[doc.documentType],
                documentId: doc.documentId,
                status: doc.status,
                fileName: doc.filePath?.split("\\").pop().split("/").pop(),
              };
            }
          });
          return updated;
        });
      } catch (err) {
        console.error("Error fetching documents:", err);
      }
    };

    fetchDocuments();
  }, [studentId]);

  const handleFileChange = (e, key) => {
    const file = e.target.files[0];
    if (!file) return;

    setDocuments(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        file,
        status: "Pending",
        fileName: file.name,
        tempPreview: URL.createObjectURL(file),
      },
    }));
  };

  const handleUpload = async (key) => {
    const file = documents[key].file;
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    const res = await axios.post(
      `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/StudentDocuments/upload/${studentId}/${key}`,
      formData
    );

    setDocuments(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        documentId: res.data.documentId,
        file: null,
        status: "Pending",
      },
    }));
  };

  const handleStatusChange = async (key, status) => {
    const documentId = documents[key].documentId;
    if (!documentId) return;

    await axios.put(
      `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/StudentDocuments/update-status/${documentId}`,
      JSON.stringify(status),
      { headers: { "Content-Type": "application/json" } }
    );

    setDocuments(prev => ({
      ...prev,
      [key]: { ...prev[key], status },
    }));
  };

  const handleView = (key) => {
    const doc = documents[key];
    setPreviewType(doc.fileName?.endsWith(".pdf") ? "pdf" : "image");
    setPreviewFile(
      doc.file
        ? doc.tempPreview
        : `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/StudentDocuments/view/${doc.documentId}`
    );
  };

  const statusColors = {
    Pending: "bg-amber-100 text-amber-700",
    Approved: "bg-green-100 text-green-700",
    Declined: "bg-red-100 text-red-700",
    Status: "bg-gray-100 text-gray-500",
  };

  const documentConfig = {
    idOrPassport: { label: "ID / Passport", description: "Government-issued identification", color: "blue" },
    proofOfFunding: { label: "Proof of Funding", description: "Bank statement or scholarship letter", color: "indigo" },
    nextOfKinID: { label: "Next of Kin ID", description: "Emergency contact identification", color: "purple" },
    proofOfResidence: { label: "Proof of Residence", description: "Utility bill or lease agreement", color: "emerald" },
  };

  const getColorStyles = (color) => {
    const colors = {
      blue: { border: "border-blue-200", bg: "bg-blue-50", text: "text-blue-600", hover: "hover:border-blue-300" },
      indigo: { border: "border-indigo-200", bg: "bg-indigo-50", text: "text-indigo-600", hover: "hover:border-indigo-300" },
      purple: { border: "border-purple-200", bg: "bg-purple-50", text: "text-purple-600", hover: "hover:border-purple-300" },
      emerald: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-600", hover: "hover:border-emerald-300" },
    };
    return colors[color] || colors.blue;
  };

  const renderCard = (key) => {
    const doc = documents[key];
    const config = documentConfig[key];
    const colors = getColorStyles(config.color);

    return (
      <div className={`bg-white rounded-lg border ${colors.border} ${colors.hover} transition-all duration-200 overflow-hidden`}>
        <div className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
            <div className="flex items-start gap-3">
              <div className={`p-2 ${colors.bg} rounded-lg`}>
                <FileText className={`w-5 h-5 ${colors.text}`} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{config.label}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{config.description}</p>
                {doc.fileName && (
                  <p className="text-xs text-gray-400 mt-1 truncate max-w-[200px]">
                    {doc.fileName}
                  </p>
                )}
              </div>
            </div>
            
            {doc.status && doc.status !== "Status" && (
              <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${statusColors[doc.status]}`}>
                {doc.status}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
            <input
              hidden
              type="file"
              ref={fileRefs[key]}
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={(e) => handleFileChange(e, key)}
            />

            <button
              onClick={() => fileRefs[key].current.click()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-800 text-white hover:bg-gray-700 transition-all duration-200"
            >
              <Upload className="w-3.5 h-3.5" />
              Upload
            </button>

            {(doc.file || doc.documentId) && (
              <button
                onClick={() => handleView(key)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-all duration-200"
              >
                <Eye className="w-3.5 h-3.5" />
                View
              </button>
            )}

            {doc.file && (
              <button
                onClick={() => handleUpload(key)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-green-600 text-white hover:bg-green-700 transition-all duration-200"
              >
                Save
              </button>
            )}

            {doc.status === "Pending" && doc.documentId && (
              <div className="flex gap-2 ml-auto">
                <button
                  onClick={() => handleStatusChange(key, "Approved")}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md bg-green-50 text-green-700 hover:bg-green-100 transition-all duration-200"
                >
                  <Check className="w-3 h-3" />
                  Approve
                </button>
                <button
                  onClick={() => handleStatusChange(key, "Declined")}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md bg-red-50 text-red-700 hover:bg-red-100 transition-all duration-200"
                >
                  <X className="w-3 h-3" />
                  Decline
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <ShieldCheck className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">Document Verification</h2>
            <p className="text-sm text-gray-500 mt-0.5">Review and manage submitted compliance documents</p>
          </div>
        </div>
      </div>

      {/* Document Cards */}
      <div className="p-5 space-y-4">
        {renderCard("idOrPassport")}
        {renderCard("proofOfFunding")}
        {renderCard("nextOfKinID")}
        {renderCard("proofOfResidence")}
      </div>

      {/* Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden">
            <div className="flex justify-between items-center px-5 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-medium text-gray-900">Document Preview</h3>
              <button
                onClick={() => setPreviewFile(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors text-xl"
              >
                ×
              </button>
            </div>
            <div className="p-4 bg-gray-50 h-[calc(85vh-60px)] overflow-auto">
              {previewType === "pdf" ? (
                <iframe
                  src={previewFile}
                  title="Document Preview"
                  className="w-full h-full rounded border border-gray-200"
                />
              ) : (
                <img
                  src={previewFile}
                  alt="Document Preview"
                  className="max-w-full max-h-full mx-auto rounded border border-gray-200"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsAdmin;